import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { ArrowUpDown, Printer, FileDown } from 'lucide-react';
import DateRangeFilter from './DateRangeFilter';
import { exportCSV, inRange } from './reportUtils';
import { useCompanyInfo, generatePrintHTML, openPrint } from './ReportExporter';

export default function CustomerReport() {
  const [loads, setLoads] = useState([]);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('Total Revenue');
  const [sortDir, setSortDir] = useState(-1);
  const company = useCompanyInfo();

  useEffect(() => {
    client.entities.Load.list('-delivery_date', 500).then(l => { setLoads(l); setLoading(false); });
  }, []);

  const filtered = loads.filter(l => inRange(l.delivery_date || l.pickup_date, start, end));

  const byCustomer = {};
  filtered.forEach(l => {
    const key = l.customer_name || 'Unknown';
    if (!byCustomer[key]) byCustomer[key] = { loads: [], lastDate: '' };
    byCustomer[key].loads.push(l);
    if ((l.delivery_date || l.pickup_date) > byCustomer[key].lastDate) {
      byCustomer[key].lastDate = l.delivery_date || l.pickup_date;
    }
  });

  let rows = Object.entries(byCustomer).map(([customer, d]) => {
    const revenue = d.loads.reduce((s, l) => s + (l.rate || 0), 0);
    const miles = d.loads.reduce((s, l) => s + (l.miles || 0), 0);
    return {
      Customer: customer,
      'Load Count': d.loads.length,
      'Total Miles': Math.round(miles),
      'Total Revenue': revenue,
      'Avg Rate/Mile': miles > 0 ? (revenue / miles).toFixed(2) : '—',
      'Last Load Date': d.lastDate || '—',
    };
  });

  rows.sort((a, b) => {
    const av = typeof a[sortKey] === 'number' ? a[sortKey] : parseFloat(a[sortKey]) || 0;
    const bv = typeof b[sortKey] === 'number' ? b[sortKey] : parseFloat(b[sortKey]) || 0;
    return sortDir * (bv - av);
  });

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d * -1);
    else { setSortKey(key); setSortDir(-1); }
  };

  const csvRows = rows.map(r => ({ ...r, 'Total Revenue': r['Total Revenue'].toFixed(2) }));

  const handlePrint = () => {
    const cols = ['Customer', 'Load Count', 'Total Miles', 'Total Revenue', 'Avg Rate/Mile', 'Last Load Date'];
    const dataRows = rows.map(r => [
      { value: r.Customer, class: 'bold' }, r['Load Count'], r['Total Miles'].toLocaleString(),
      { value: `$${r['Total Revenue'].toLocaleString()}`, class: 'positive' },
      typeof r['Avg Rate/Mile'] === 'string' && r['Avg Rate/Mile'] !== '—' ? { value: `$${r['Avg Rate/Mile']}`, class: 'gold' } : r['Avg Rate/Mile'],
      r['Last Load Date'],
    ]);
    const grandRevenue = rows.reduce((s, r) => s + r['Total Revenue'], 0);
    const grandLoads = rows.reduce((s, r) => s + r['Load Count'], 0);
    const totalsRow = ['TOTALS', grandLoads, '', { value: `$${grandRevenue.toLocaleString()}`, class: 'gold' }, '', ''];
    const cards = [
      { label: 'Customers', value: rows.length, color: 'gold' },
      { label: 'Total Loads', value: grandLoads, color: '' },
      { label: 'Total Revenue', value: `$${grandRevenue.toLocaleString()}`, color: 'positive' },
    ];
    const html = generatePrintHTML('Customer Report', `${start || 'All'} – ${end || 'All'}`, cols, dataRows, totalsRow, company, cards);
    openPrint(html);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <DateRangeFilter onChange={(s, e) => { setStart(s); setEnd(e); }} onExport={() => exportCSV(csvRows, 'customer-report.csv')} exportDisabled={loading || rows.length === 0} />
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><Printer className="w-4 h-4" /> Print</button>
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><FileDown className="w-4 h-4" /> Export PDF</button>
      </div>
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {['Customer', 'Load Count', 'Total Miles', 'Total Revenue', 'Avg Rate/Mile', 'Last Load Date'].map(h => (
                <th key={h} onClick={() => toggleSort(h)} className="text-left px-3 py-2.5 text-muted-foreground font-medium whitespace-nowrap cursor-pointer hover:text-foreground select-none">
                  <span className="flex items-center gap-1">{h} <ArrowUpDown className="w-3 h-3 opacity-50" /></span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map(r => (
              <tr key={r.Customer} className="hover:bg-white/3">
                <td className="px-3 py-2.5 font-semibold text-foreground">{r.Customer}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r['Load Count']}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r['Total Miles'].toLocaleString()}</td>
                <td className="px-3 py-2.5 font-semibold text-green-400">${r['Total Revenue'].toLocaleString()}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{typeof r['Avg Rate/Mile'] === 'number' ? `$${r['Avg Rate/Mile']}` : r['Avg Rate/Mile']}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r['Last Load Date']}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && !loading && <p className="text-center text-muted-foreground py-8 text-sm">No customer data for this period.</p>}
        {loading && <p className="text-center text-muted-foreground py-8 text-sm">Loading...</p>}
      </div>
    </div>
  );
}